// BL598 Phase 2 — `gridctl create grid <slug>`.
//
// Creates a new grid (org). The caller becomes its admin and a default
// "general" space is provisioned server-side. Wraps POST /api/v2/orgs.
//
// The server derives provisioning (wildcard TLS) from org-create; it returns
// 201 with the org when the cert settled inside the sync window, or 202 with a
// `poll_url` when the cert is still issuing in the background (backlog 255).
// The CLI surfaces both as success — the grid exists either way.

import { Command } from 'commander';
import type { IV2Client } from '../../api/v2-client.js';
import { getV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { CloudGridError } from '../../utils.js';

// Mirror the server's slug shape (orgs-v2 validateCreateOrgBody): 3-40 chars,
// lowercase a-z0-9-, must start with a letter. The server re-validates; this
// is a fast local guard so an obvious typo fails before the round-trip.
const SLUG_RE = /^[a-z][a-z0-9-]{2,39}$/;

export interface CreateGridOptions {
  name?: string;
  json?: boolean;
}

interface CreatedGrid {
  org_id?: string;
  slug: string;
  name: string;
  status?: string;
  poll_url?: string;
}

export async function runCreateGrid(
  client: IV2Client,
  slug: string,
  opts: CreateGridOptions,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false });
  if (!SLUG_RE.test(slug)) {
    throw new CloudGridError(
      `Invalid grid slug '${slug}'. Use 3-40 lowercase letters, digits, or hyphens, starting with a letter.`,
    );
  }

  const grid = await client.post<CreatedGrid>('/api/v2/orgs', {
    name: opts.name?.trim() || slug,
    slug,
  });

  out.json(() => grid);
  out.human(() => {
    const lines = [`Created grid ${grid.slug}.`];
    if (grid.poll_url) {
      lines.push('Wildcard TLS is still provisioning. Check it with:');
      lines.push(`  gridctl describe grid ${grid.slug}`);
    }
    lines.push(`Switch to it: gridctl use ${grid.slug}`);
    return lines.join('\n');
  });
}

export const createGridCommand = new Command('grid')
  .description('Create a new grid (you become its admin)')
  .argument('<slug>', 'Grid slug (3-40 lowercase letters, digits, hyphens)')
  .option('--name <name>', 'Display name (defaults to the slug)')
  .option('--json', 'machine-readable output')
  .action(async (slug: string, opts: CreateGridOptions) => {
    try {
      const client = await getV2Client();
      await runCreateGrid(client, slug, opts);
    } catch (err) {
      await handleV2Error(err, { json: !!opts.json });
    }
  });
